import { createFileRoute, Link } from "@tanstack/react-router";

import { createHead } from "@/components/createHead";
import { CustomNodeInstallStats } from "@/components/CustomNodeInstallStats";
import { FeedEditor } from "@/components/FeedEditor";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { MarketplaceBody } from "@/components/MarketplacePage";
import { Avatar } from "@/components/ui/avatar";
import { ButtonLink } from "@/components/ui/button";
import { CustomNodeHistoryItem, getCustomNodeQueryOptions } from "@/lib/api";
import { FeedVersion } from "@/lib/types";

export const Route = createFileRoute(
  "/_marketplace-layout/custom-nodes/$nodeId",
)({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { nodeId } }) => {
    const data = await queryClient.ensureQueryData(
      getCustomNodeQueryOptions(parseInt(nodeId)),
    );

    return {
      node: data as Omit<CustomNodeHistoryItem, "component_metadata">,
    };
  },
  head: ({ loaderData }) => {
    const node = loaderData?.node as CustomNodeHistoryItem;

    return createHead({
      title: `${node.title} | Graze`,
      ogTitle: `${node.title} | Graze`,
      description: node.description,
      pathname: `/custom-nodes/${node.id}`,
      ogImage: `/custom-nodes/$nodeId/og.png`,
      ogParams: { nodeId: node.id },
    });
  },
});

function RouteComponent() {
  const { node } = Route.useLoaderData();

  return (
    <MarketplaceBody>
      <div className="pt-4 md:pt-20 md:pb-12 flex flex-col gap-4 md:gap-8 items-center">
        <CustomNodeInstallStats {...node} size="lg" />
        <h1 className="text-5xl md:text-8xl font-bold instrument-serif text-center">
          {node.title}
        </h1>
        <div className="flex flex-col gap-4 items-center">
          <div className="flex items-center gap-2">
            <Avatar src={node.user.avatar} alt={node.user.handle} />
            <Link
              className="text-lg md:text-xl text-slate-dim"
              to="/user/$userId"
              params={{ userId: node.graze_user_id.toString() }}
            >
              @{node.user.handle}
            </Link>
          </div>
          <ButtonLink
            to="/app/custom-nodes/$nodeId/view"
            params={{ nodeId: node.id.toString() }}
            variant="outline"
            size="sm"
          >
            View in App
          </ButtonLink>
        </div>
      </div>

      {node.description && (
        <MarkdownRenderer className="text-slate-dim text-lg">
          {node.description}
        </MarkdownRenderer>
      )}

      <FeedEditor
        feedVersion={{ manifest: { filter: node.component } } as FeedVersion}
        rootNode={{
          title: "Custom Node",
          variant: "custom-node",
          description:
            "This is a definition of a custom node that you can share with other feeds.",
          customNodeParameters: node.component.metadata?.customNodeParameters,
        }}
      />
    </MarketplaceBody>
  );
}
